import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import LoadingSpinner from 'components/Shared/icons/LoadingSpinner';

interface TwitterEmbedProps {
  XProfileUsername: string;
}

export default function TwitterEmbed({ XProfileUsername }: TwitterEmbedProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [errorState, setErrorState] = useState<{
    hasError: boolean;
    errorType?: 'network' | 'rate_limit' | 'not_found' | 'unknown';
    message?: string;
  }>({ hasError: false });
  const [twitterTimeLineEmbed, setTwitterTimeLineEmbed] = useState<string>('');
  const [isContentInjected, setIsContentInjected] = useState(false);
  
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Memoized content injection function
  const injectContent = useCallback((htmlContent: string) => {
    if (!iframeRef.current) return false;
    
    const iframe = iframeRef.current;
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    
    if (iframeDoc) {
      try {
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        setIsContentInjected(true);
        setIsLoading(false);
        return true;
      } catch (error) {
        console.error('Error injecting content into iframe:', error);
        return false;
      }
    }
    return false;
  }, []);

  useEffect(() => {
    const fetchEmbed = async () => {
      if (!XProfileUsername) return;
      
      setIsLoading(true);
      setErrorState({ hasError: false });
      setIsContentInjected(false);
      
      try {
        // Use relative path instead of process.env.PUBLIC_URL
        const response = await axios.get('/api/embed-twitter', {
          params: {
            XProfileUsername: XProfileUsername,
            XProfileLanguage: 'en' // You might want to make this configurable
          },
          timeout: 10000, // 10 second timeout
        });

        if (response.data) {
          setTwitterTimeLineEmbed(response.data);
          setErrorState({ hasError: false });
          
          // Try to inject content immediately if iframe is ready
          if (iframeRef.current) {
            injectContent(response.data);
          }
        } else {
          throw new Error('Empty response from server');
        }

      } catch (err: any) {
        console.error('Error fetching Twitter embed:', err);
        
        // More specific error handling
        let errorType: 'network' | 'rate_limit' | 'not_found' | 'unknown' = 'unknown';
        let message = 'Something went wrong loading the Twitter embed';
        
        if (err.response?.status === 429) {
          errorType = 'rate_limit';
          message = 'Rate limit exceeded. Please try again in a few minutes.';
        } else if (err.response?.status === 404) {
          errorType = 'not_found';
          message = 'Twitter profile not found or private.';
        } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
          errorType = 'network';
          message = 'Request timed out. Please check your connection and try again.';
        } else if (!navigator.onLine) {
          errorType = 'network';
          message = 'No internet connection. Please check your network and try again.';
        }
        
        setErrorState({ hasError: true, errorType, message });
        setTwitterTimeLineEmbed('');
        setIsLoading(false);
      }
    };

    fetchEmbed();
  }, [XProfileUsername, injectContent]);

  // Handle content injection when iframe loads
  const handleIframeLoad = useCallback(() => {
    if (twitterTimeLineEmbed && !isContentInjected) {
      injectContent(twitterTimeLineEmbed);
    }
  }, [twitterTimeLineEmbed, isContentInjected, injectContent]);

  return (
    <div className='relative h-full min-h-[528px] lg:min-h-0 overflow-hidden'>
      {/* Loader overlay - show until content is actually injected */}
      {isLoading && (
        <div className="absolute inset-0 z-10 grid place-items-center bg-white bg-opacity-80">
          <LoadingSpinner />
        </div>
      )}

      {
        errorState.hasError ? (
          <div className="absolute inset-0 z-10 grid place-items-center">
            <p className='text-red-600 font-bold'>{errorState.message}</p>
          </div>
        ) : (
          // {/* Always render iframe, content will be injected */}
          <iframe
            ref={iframeRef}
            title={`Twitter Timeline for ${XProfileUsername}`}
            style={{ width: '100%', height: '100%', border: 'none' }}
            className="min-h-[528px] lg:min-h-0 overflow-hidden"
            sandbox="allow-scripts"
            onLoad={handleIframeLoad}
            onError={(e) => {
              console.error('Iframe error:', e);
              setErrorState({ 
                hasError: true, 
                errorType: 'unknown',
                message: 'Failed to load Twitter embed frame'
              });
              setIsLoading(false);
            }}
          />
        )
      }

    </div>
  );
}